import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import SideSheet, {
    SideSheetButtons,
    SideSheetContent,
    SideSheetContentPadding,
    SideSheetForm,
    SideSheetSection,
    SideSheetSections,
    useSideSheet,
    useSideSheetSection,
} from "../../components/SideSheet";
import {
    FormFieldErrors,
    hasErrors,
    useForm,
    validateMandatoryField,
    validateNumberField,
} from "../../utils/form";
import { useUserInfo } from "../../utils/security";
import { wait } from "../../utils/mock";
import NumberField from "../../components/NumberField";
import { formatCurrency, formatNum, parseNum } from "../../utils/numbers";
import { DatePicker } from "@mui/x-date-pickers";
import {
    AddOutlined,
    CheckOutlined,
    ClearOutlined,
    CloseOutlined,
    ContentCopyOutlined,
    EditOutlined,
    HourglassBottomOutlined,
    PrintOutlined,
    SearchOutlined,
} from "@mui/icons-material";
import PaymentInstructions from "./PaymentInstructions";
import { isEmpty } from "lodash";
import { TransactionItems } from "./TransactionItems";
import TransactionItemDialog from "./TransactionItemDialog";
import TransactionCounterpartyDialog from "./TransactionCounterpartyDialog";
import TaxItemDialog from "./TaxItemDialog";
import CurrencyField from "../../components/CurrencyField";
import { TaxItems } from "./TaxItems";
import calculateTax from "./calculateTax";
import dayjs from "dayjs";
import Actions from "../../components/Actions";
import TransactionStatusDialog from "./TransactionStatusDialog";
import RecurringTransactionAlert from "./RecurringTransactionAlert";

export const TransactionModal = ({ data, onTransactionSaved, open, hide, dataLoading }) => {
    const [loading, setLoading] = useState(false);
    const { userInfo } = useUserInfo();

    const onSubmit = useCallback(
        async (data) => {
            setLoading(true);
            try {
                await wait(2134);
                onTransactionSaved && (await onTransactionSaved(data));
            } finally {
                setLoading(false);
            }
        },
        [onTransactionSaved, setLoading]
    );

    const validate = useCallback((values, errors) => {
        validateMandatoryField(values.name, "name", errors);
        validateMandatoryField(values.direction, "direction", errors);
        validateMandatoryField(values.date, "date", errors);

        //TODO implement properly
        if (values.hasIdentificator) {
            validateMandatoryField(values.idNumber, "idNumber", errors);
        }
        if (values.hasDueDate) {
            validateMandatoryField(values.dueDate, "dueDate", errors);
        }
        if (!values.hasItems) {
            validateMandatoryField(values.amount, "amount", errors);
            validateNumberField(values.amount, "amount", errors);
        }

        return errors;
    }, []);

    const mapFieldsToData = useCallback(
        ({ percentage, date, dueDate, ...values }) => ({
            percentage: parseNum(percentage, userInfo.decimalDelimiter, userInfo.thousandDelimiter),
            date: date ? dayjs(date).format("YYYY-MM-DD") : undefined,
            dueDate: dueDate ? dayjs(dueDate).format("YYYY-MM-DD") : undefined,
            ...values,
        }),
        [userInfo]
    );
    const mapDataToFields = useCallback(
        ({ percentage, date, dueDate, ...values }) => ({
            percentage: formatNum(
                percentage,
                userInfo.decimalDelimiter,
                userInfo.thousandDelimiter
            ),
            date: date ? dayjs(date, "YYYY-MM-DD") : null,
            dueDate: dueDate ? dayjs(dueDate, "YYYY-MM-DD") : null,
            ...values,
        }),
        [userInfo]
    );

    const { handleSubmit, handleChange, values, errors, setValues } = useForm({
        onSubmit,
        validate,
        data,
        mapFieldsToData,
        mapDataToFields,
    });

    const isRecurring = !isEmpty(values.recurringSettings);
    const sideSheetSection = useSideSheetSection();
    const actions = [];
    if (!dataLoading) {
        if (values.id && values.printable) {
            actions.push({
                title: "Stáhnout PDF",
                icon: <PrintOutlined />,
            });
        }

        if (!isRecurring && values.id) {
            actions.push({
                title: "Duplikovat transakci...",
                icon: <ContentCopyOutlined />,
                onClick: () =>
                    setValues({
                        ...data,
                        idNumber: undefined,
                        name: undefined,
                        id: undefined,
                        _source: {
                            name: data.name || "N/A",
                            idNumber: data.idNumber || "N/A",
                            transactionTypeName: data.transactionTypeName,
                        },
                    }),
            });
        }
    }

    return (
        <SideSheet
            title={dataLoading ? <Skeleton height={36} /> : values.transactionTypeName}
            open={open}
            hide={hide}
            loading={loading || dataLoading}
            actions={actions}>
            {!dataLoading ? (
                <SideSheetForm onSubmit={handleSubmit}>
                    <SideSheetContent noPadding>
                        <SideSheetContentPadding>
                            {!isEmpty(values._source) && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Kopie transakce {values._source.name || "N/A"} (
                                    {values._source.transactionTypeName})
                                </Alert>
                            )}
                            {isRecurring && (
                                <Box my={2}>
                                    <RecurringTransactionAlert
                                        count={values.recurringSettings?.count}
                                        frequency={values.recurringSettings?.frequency}
                                        date={values.recurringSettings?.date}
                                        name={values.name}
                                        amount={values.amount}
                                        currencyCode={values.currencyCode}
                                    />
                                </Box>
                            )}

                            <TextField
                                value={values.name || ""}
                                onChange={handleChange}
                                disabled={loading}
                                name="name"
                                label="Název"
                                placeholder="Název transakce"
                                fullWidth
                                margin="normal"
                                required
                                error={hasErrors(errors, "name")}
                                helperText={<FormFieldErrors errors={errors} field="name" />}
                            />
                            {values.hasIdentificator && (
                                <TextField
                                    value={values.idNumber || ""}
                                    onChange={handleChange}
                                    disabled={loading}
                                    name="idNumber"
                                    label={values.identificatorLabel || "Identifikátor transakce"}
                                    placeholder="Identifikátor transakce"
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={hasErrors(errors, "idNumber")}
                                    helperText={
                                        <FormFieldErrors errors={errors} field="idNumber" />
                                    }
                                />
                            )}
                            {!isRecurring && (
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="flex-start"
                                    sx={{ mt: 2, mb: 2 }}>
                                    <DatePicker
                                        onChange={(date) => {
                                            setValues({
                                                ...values,
                                                date,
                                                fiscalYear: date?.year() || undefined,
                                                dueDate:
                                                    date &&
                                                    values.hasDueDate &&
                                                    values.defaultDueDateDays
                                                        ? date.add(
                                                              values.defaultDueDateDays,
                                                              "days"
                                                          )
                                                        : undefined,
                                            });
                                        }}
                                        value={values.date || null}
                                        disabled={loading}
                                        label={values.hasDueDate ? "Datum vydání" : "Datum"}
                                        format={userInfo.dateFormat}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                required: true,
                                                error: hasErrors(errors, "date"),
                                            },
                                        }}
                                        helperText={
                                            <FormFieldErrors errors={errors} field="date" />
                                        }
                                    />

                                    <NumberField
                                        value={values.fiscalYear || ""}
                                        onChange={handleChange}
                                        disabled={loading}
                                        name="fiscalYear"
                                        label="Účetní rok"
                                        placeholder="Účetní rok transakce"
                                        fullWidth
                                        required
                                        error={hasErrors(errors, "fiscalYear")}
                                        helperText={
                                            <FormFieldErrors errors={errors} field="fiscalYear" />
                                        }
                                    />
                                </Stack>
                            )}
                            {values.hasDueDate && (
                                <Box mb={1} mt={2}>
                                    <DatePicker
                                        onChange={(dueDate) => setValues({ ...values, dueDate })}
                                        value={values.dueDate || null}
                                        disabled={loading}
                                        label="Datum splatnosti"
                                        format={userInfo.dateFormat}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                required: true,
                                                error: hasErrors(errors, "dueDate"),
                                            },
                                        }}
                                    />
                                    <FormFieldErrors errors={errors} field="dueDate" />
                                </Box>
                            )}
                            {!!userInfo.projectsSupport && !!values.hasProjectReference && (
                                <TextField
                                    value={values.projectName || ""}
                                    onChange={handleChange}
                                    disabled={loading}
                                    name="projectName"
                                    label="Projekt"
                                    placeholder="Vyberte projekt"
                                    fullWidth
                                    margin="normal"
                                    error={hasErrors(errors, "projectName")}
                                    helperText={
                                        <FormFieldErrors errors={errors} field="projectName" />
                                    }
                                />
                            )}
                            {!!userInfo.employeesSupport && !!values.hasEmployeeReference && (
                                <TextField
                                    value={values.employeeName || ""}
                                    onChange={handleChange}
                                    disabled={loading}
                                    name="employeeName"
                                    label="Zaměstnanec"
                                    placeholder="Vyberte zaměstnance"
                                    fullWidth
                                    margin="normal"
                                    error={hasErrors(errors, "employeeName")}
                                    helperText={
                                        <FormFieldErrors errors={errors} field="employeeName" />
                                    }
                                />
                            )}
                            {!values.hasItems && (
                                <CurrencyField
                                    value={values.amount || ""}
                                    onChange={(e) => {
                                        const amount = e.target.value;
                                        const fieldValues = mapFieldsToData({ ...values, amount });
                                        setValues({
                                            ...mapDataToFields(calculateTransaction(fieldValues)),
                                            amount,
                                        });
                                    }}
                                    currencyCode={values.currencyCode}
                                    disabled={loading}
                                    name="amount"
                                    label="Částka"
                                    placeholder="Zadejte částku"
                                    fullWidth
                                    margin="normal"
                                    required
                                    error={hasErrors(errors, "amount")}
                                    helperText={<FormFieldErrors errors={errors} field="amount" />}
                                />
                            )}
                        </SideSheetContentPadding>
                        <SideSheetSections>
                            {!!values.hasStatus && (
                                <StatusSection
                                    paid={values.paid}
                                    paymentDate={values.paymentDate}
                                    dueDate={values.dueDate || values.date}
                                    onChange={(vals) => setValues({ ...values, ...vals })}
                                    disabled={loading}
                                />
                            )}
                            {!!values.hasSubjectInfo && (
                                <SubjectSection
                                    {...sideSheetSection("subject")}
                                    value={values}
                                    onChange={(subject) =>
                                        setValues({
                                            ...values,
                                            subjectName: subject.name,
                                            subjectAddress: subject.address,
                                            subjectIdNumber: subject.idNumber,
                                            subjectTaxIdNumber: subject.taxIdNumber,
                                        })
                                    }
                                    disabled={loading}
                                />
                            )}
                            {!!values.hasCpty && (
                                <CounterpartySection
                                    {...sideSheetSection("counterparty")}
                                    value={values}
                                    onChange={(cpty) =>
                                        setValues({
                                            ...values,
                                            cptyId: cpty?.id,
                                            cptyName: cpty?.name,
                                            cptyAddress: cpty?.address,
                                            cptyIdNumber: cpty?.idNumber,
                                            cptyTaxIdNumber: cpty?.taxIdNumber,
                                        })
                                    }
                                    disabled={loading}
                                />
                            )}
                            {!!values.hasItems && (
                                <TransactionItemsSection
                                    {...sideSheetSection("items")}
                                    currencyCode={values.currencyCode}
                                    value={values.items}
                                    onChange={(items) => {
                                        const fieldValues = mapFieldsToData({ ...values, items });
                                        setValues(
                                            mapDataToFields(calculateTransaction(fieldValues))
                                        );
                                    }}
                                    disabled={loading}
                                />
                            )}
                            {!!values.hasPaymentInstructions && (
                                <SideSheetSection
                                    title="Platební instrukce"
                                    {...sideSheetSection("paymentInstructions")}
                                    renderDescription={() =>
                                        isEmpty(values.paymentMethods)
                                            ? "Žádné platební instrukce"
                                            : values.paymentMethods[0].name
                                    }>
                                    <PaymentInstructions
                                        value={values.paymentMethods}
                                        onChange={(paymentMethods) =>
                                            setValues({ ...values, paymentMethods })
                                        }
                                        editable
                                        disabled={loading}
                                        idNumberLabel={
                                            values.identificatorLabel || "Číslo transakce"
                                        }
                                    />
                                </SideSheetSection>
                            )}
                            {!!userInfo.transactionsTaxSupport &&
                                !values.hasItems &&
                                !!values.hasTax && (
                                    <TaxSection
                                        {...sideSheetSection("tax")}
                                        value={values.taxItems}
                                        base={values.amount}
                                        currencyCode={values.currencyCode}
                                        onChange={(taxItems) => setValues({ ...values, taxItems })}
                                        disabled={loading}
                                    />
                                )}
                            {!!values.hasItems ? (
                                <TransactionItemsTotalSection
                                    hasTax={values.hasTax && userInfo.transactionsTaxSupport}
                                    items={values.items}
                                    currencyCode={values.currencyCode}
                                />
                            ) : (
                                <SimpleTransactionTotalSection
                                    total={values.amount}
                                    taxItems={values.taxItems}
                                    currencyCode={values.currencyCode}
                                    hasTax={values.hasTax && userInfo.transactionsTaxSupport}
                                />
                            )}
                        </SideSheetSections>

                        <SideSheetContentPadding></SideSheetContentPadding>
                    </SideSheetContent>
                    <SideSheetButtons>
                        <Button variant="outlined" onClick={(e) => hide?.()} disabled={loading}>
                            Storno
                        </Button>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            disabled={loading}
                            loading={loading}>
                            Uložit
                        </LoadingButton>
                    </SideSheetButtons>
                </SideSheetForm>
            ) : (
                <SideSheetContent>
                    <Stack variant="vertical" gap={2}>
                        <Skeleton variant="rectangular" height={400} />
                        <Skeleton variant="rectangular" height={120} />
                        <Skeleton variant="rectangular" height={120} />
                        <Stack direction="row" spacing={2}></Stack>
                    </Stack>
                </SideSheetContent>
            )}
        </SideSheet>
    );
};

const calculateTaxItems = (items, base) => {
    return (items || []).map((taxItem) => {
        return {
            ...taxItem,
            ...calculateTax(taxItem.percentage, base),
        };
    });
};

const calculateTransaction = (fieldValues) => {
    let total = 0;
    let tax = 0;
    if (fieldValues.hasItems) {
        if (Array.isArray(fieldValues.items)) {
            fieldValues.items.forEach((item) => {
                total += item.total;
                item.taxItems = calculateTaxItems(item.taxItems, item.total);
                tax += (item.taxItems || []).reduce((acc, taxItem) => acc + taxItem.total, 0);
            });
        }
    } else {
        total = fieldValues.amount || 0;
        fieldValues.taxItems = calculateTaxItems(fieldValues.taxItems, total);
        tax = (fieldValues.taxItems || []).reduce((acc, taxItem) => acc + taxItem.total, 0);
    }

    return {
        ...fieldValues,
        total,
        tax,
        totalPayable: total + tax,
    };
};

//TODO implement all sections

const StatusSection = ({ disabled, paid, paymentDate, dueDate, onChange }) => {
    const { userInfo } = useUserInfo();
    const avatarSize = 48;
    const statusModal = useSideSheet();

    let contentData = {};
    if (paid) {
        const days = paymentDate && dueDate && dayjs(paymentDate).diff(dueDate, "day");

        contentData = {
            avatar: (
                <Avatar
                    sx={(theme) => ({
                        bgcolor: theme.palette.success.light,
                        width: avatarSize,
                        height: avatarSize,
                    })}>
                    <CheckOutlined />
                </Avatar>
            ),
            title: "Zaplaceno",
            description: `Zaplaceno ${dayjs(paymentDate).format(userInfo.dateFormat)} (počet dnů ${
                days > 0 ? "po splatnosti" : "před splatností"
            }: ${Math.abs(days)})`,
        };
    } else if (dueDate && dayjs().isAfter(dueDate)) {
        contentData = {
            avatar: (
                <Avatar
                    sx={(theme) => ({
                        bgcolor: theme.palette.error.light,
                        width: avatarSize,
                        height: avatarSize,
                    })}>
                    <CloseOutlined />
                </Avatar>
            ),
            title: "Po splatnosti",
            description: `Počet dnů po splatnosti: ${dayjs().diff(dueDate, "day")} (${dayjs(
                dueDate
            ).format(userInfo.dateFormat)})`,
        };
    } else {
        contentData = {
            avatar: (
                <Avatar
                    sx={(theme) => ({
                        width: avatarSize,
                        height: avatarSize,
                    })}>
                    <HourglassBottomOutlined />
                </Avatar>
            ),
            title: "Čeká na zaplacení",
            description: `Počet dnů do splatnosti: ${-1 * dayjs().diff(dueDate, "day")} (${dayjs(
                dueDate
            ).format(userInfo.dateFormat)})`,
        };
    }

    return (
        <>
            <SideSheetSection open>
                <Box sx={{ pl: 1 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <>
                            {contentData?.avatar}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6">{contentData?.title}</Typography>
                                <Typography
                                    variant="caption"
                                    color={disabled ? "text.disabled" : "secondary"}
                                    component="div">
                                    {contentData?.description}
                                </Typography>
                            </Box>
                        </>
                        <Box sx={{ flex: 0 }}>
                            <Actions
                                actions={[
                                    {
                                        title: "Změnit stav",
                                        icon: <EditOutlined />,
                                        onClick: () => statusModal.show({ paid, paymentDate }),
                                    },
                                ]}
                                disabled={disabled}
                            />
                        </Box>
                    </Box>
                </Box>
            </SideSheetSection>
            <TransactionStatusDialog {...statusModal} onChange={onChange} />
        </>
    );
};

const TransactionItemsSection = ({ value, onChange, disabled, currencyCode, ...rest }) => {
    const itemModal = useSideSheet();
    return (
        <>
            <SideSheetSection
                title="Položky"
                {...rest}
                renderDescription={() =>
                    isEmpty(value) ? "Žádné položky" : `${value.length} položek`
                }
                actions={[
                    {
                        title: "Přidat položku",
                        icon: <AddOutlined />,
                        onClick: () => itemModal.show({}),
                    },
                ]}
                disabled={disabled}>
                <TransactionItems
                    items={value || []}
                    currencyCode={currencyCode}
                    disabled={disabled}
                    onAdd={() => itemModal.show({})}
                    onEdit={(item, index) => itemModal.show({ ...item, _idx: index })}
                    onDelete={(index) => onChange?.(value.filter((_, i) => i !== index))}
                />
            </SideSheetSection>

            <TransactionItemDialog
                {...itemModal}
                onChange={({ _idx, ...item }) => {
                    if (_idx === undefined) {
                        onChange?.([...(value || []), item]);
                        return;
                    }
                    onChange?.(value.map((i, idx) => (idx === _idx ? item : i)));
                }}
            />
        </>
    );
};

const CounterpartySection = ({ value, onChange, disabled, ...rest }) => {
    const subjectModal = useSideSheet();
    const actions = [];

    if (isEmpty(value.cptyId)) {
        actions.push({
            title: "Vyhledat",
            icon: <SearchOutlined />,
            onClick: () =>
                subjectModal.show({
                    mode: "existing",
                }),
        });
    }
    actions.push({
        title: "Upravit",
        icon: <EditOutlined />,
        onClick: () =>
            subjectModal.show({
                mode: isEmpty(value.cptyId) ? "non-existing" : "existing",
                id: value.cptyId,
                name: value.cptyName,
                address: value.cptyAddress,
                idNumber: value.cptyIdNumber,
                taxIdNumber: value.cptyTaxIdNumber,
            }),
    });
    actions.push({
        title: "Odebrat",
        icon: <ClearOutlined />,
        onClick: () => {
            onChange?.();
        },
    });

    return (
        <>
            <SideSheetSection
                title={value.counterpartyLabel || "Protistrana"}
                {...rest}
                renderDescription={() => value.cptyName || "Nevyplněno"}
                disabled={disabled}
                actions={actions}>
                {isEmpty(value.cptyName) && isEmpty(value.cptyId) ? (
                    <Box>
                        <Typography variant="caption" gutterBottom component="div">
                            Nevyplněno
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Typography component="div" gutterBottom>
                            {value.cptyName}
                        </Typography>
                        <Typography
                            component="div"
                            sx={{ whiteSpace: "pre" }}
                            variant="caption"
                            gutterBottom>
                            {value.cptyAddress}
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Typography component="div" sx={{ flex: 1 }} variant="caption">
                                IČ: {value.cptyIdNumber}
                            </Typography>
                            <Typography component="div" sx={{ flex: 1 }} variant="caption">
                                DIČ: {value.cptyTaxIdNumber}
                            </Typography>
                        </Stack>
                    </Box>
                )}
            </SideSheetSection>
            <TransactionCounterpartyDialog
                {...subjectModal}
                onChange={onChange}
                disabled={disabled}
                title={value.counterpartyLabel || "Protistrana"}
            />
        </>
    );
};

const SubjectSection = ({ value, onChange, disabled, ...rest }) => {
    const subjectModal = useSideSheet();
    return (
        <>
            <SideSheetSection
                title={value.subjectLabel || "Subjekt"}
                {...rest}
                renderDescription={() => value.subjectName || "Nevyplněno"}
                disabled={disabled}
                actions={[
                    {
                        title: "Upravit",
                        icon: <EditOutlined />,
                        onClick: () => {
                            subjectModal.show({
                                name: value.subjectName,
                                address: value.subjectAddress,
                                idNumber: value.subjectIdNumber,
                                taxIdNumber: value.subjectTaxIdNumber,
                            });
                        },
                    },
                ]}>
                <Box>
                    <Typography component="div" gutterBottom>
                        {value.subjectName}
                    </Typography>
                    <Typography
                        component="div"
                        sx={{ whiteSpace: "pre" }}
                        variant="caption"
                        gutterBottom>
                        {value.subjectAddress}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <Typography component="div" sx={{ flex: 1 }} variant="caption">
                            IČ: {value.subjectIdNumber}
                        </Typography>
                        <Typography component="div" sx={{ flex: 1 }} variant="caption">
                            DIČ: {value.subjectTaxIdNumber}
                        </Typography>
                    </Stack>
                </Box>
            </SideSheetSection>
            <SubjectDialog
                {...subjectModal}
                onChange={onChange}
                disabled={disabled}
                title={value.subjectLabel || "Subjekt"}
            />
        </>
    );
};

const SubjectDialog = ({ data, onChange, open, hide, disabled, title }) => {
    const { userInfo } = useUserInfo();

    const { values, handleChange, setValues, handleSubmit, errors } = useForm({
        data,
        onSubmit: (values) => {
            onChange(values);
            hide();
        },
        validate: (values, errors) => {
            validateMandatoryField(values.name, "name", errors);
        },
    });

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={hide}>
            <DialogTitle>{title || "Subjekt"}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={hide}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}>
                <CloseOutlined />
            </IconButton>
            <DialogContent>
                <TextField
                    label="Název"
                    name="name"
                    value={values.name || ""}
                    onChange={handleChange}
                    required
                    margin="normal"
                    fullWidth
                    error={hasErrors(errors, "name")}
                    helperText={<FormFieldErrors errors={errors} field="name" />}
                />

                <TextField
                    label="Adresa"
                    name="address"
                    value={values.address || ""}
                    onChange={handleChange}
                    margin="normal"
                    fullWidth
                    multiline
                    rows={4}
                    error={hasErrors(errors, "address")}
                    helperText={<FormFieldErrors errors={errors} field="address" />}
                />

                <Stack direction="row" spacing={2} sx={{ my: 2 }}>
                    <TextField
                        label="IČ"
                        name="idNumber"
                        value={values.idNumber || ""}
                        onChange={handleChange}
                        margin="normal"
                        fullWidth
                        error={hasErrors(errors, "idNumber")}
                        helperText={<FormFieldErrors errors={errors} field="idNumber" />}
                    />
                    <TextField
                        label="DIČ"
                        name="taxIdNumber"
                        value={values.taxIdNumber || ""}
                        onChange={handleChange}
                        margin="normal"
                        fullWidth
                        error={hasErrors(errors, "taxIdNumber")}
                        helperText={<FormFieldErrors errors={errors} field="taxIdNumber" />}
                    />
                </Stack>
                <Button onClick={() => setValues(userInfo?.financesSettings || {})}>
                    Nastavit výchozí hodnoty
                </Button>
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3 }}>
                <Button onClick={hide} disabled={disabled}>
                    Storno
                </Button>
                <Button variant="contained" disabled={disabled} onClick={handleSubmit}>
                    Uložit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TaxSection = ({ value, onChange, base, currencyCode, disabled, ...rest }) => {
    const itemModal = useSideSheet();

    return (
        <>
            <SideSheetSection
                title="Daň"
                {...rest}
                renderDescription={() => (isEmpty(value) ? "Žádná daň" : `${value[0].taxTypeName}`)}
                actions={[
                    {
                        title: "Přidat daň",
                        icon: <AddOutlined />,
                        onClick: () =>
                            itemModal.show({
                                base: base || 0,
                            }),
                    },
                ]}
                disabled={disabled}>
                <TaxItems
                    items={value || []}
                    disabled={disabled}
                    currencyCode={currencyCode}
                    onAdd={() => itemModal.show({ base: base || 0 })}
                    onEdit={(item, index) => itemModal.show({ ...item, _idx: index })}
                    onDelete={(index) => onChange?.(value.filter((_, i) => i !== index))}
                />
            </SideSheetSection>
            <TaxItemDialog
                {...itemModal}
                currencyCode={currencyCode}
                onChange={({ _idx, ...item }) => {
                    if (_idx === undefined) {
                        onChange?.([...(value || []), item]);
                        return;
                    }
                    onChange?.(value.map((i, idx) => (idx === _idx ? item : i)));
                }}
            />
        </>
    );
};

const SimpleTransactionTotalSection = ({ total, taxItems, currencyCode, disabled, hasTax }) => {
    const { userInfo } = useUserInfo();
    let tax = (taxItems || []).reduce((acc, taxItem) => acc + taxItem.total, 0);

    return (
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
            <Box>
                <Box>
                    <Typography variant="caption" color={disabled ? "disabled" : "secondary"}>
                        Celkem
                    </Typography>
                    <Typography variant="h6" color={disabled ? "disabled" : "text.primary"}>
                        {formatCurrency(
                            total,
                            currencyCode,
                            userInfo.currencies,
                            2,
                            userInfo.decimalDelimiter,
                            userInfo.thousandDelimiter
                        )}
                    </Typography>
                    {!!hasTax && (
                        <Box>
                            <Typography
                                variant="caption"
                                color={disabled ? "disabled" : "secondary"}>
                                + daň{" "}
                                {formatCurrency(
                                    tax,
                                    currencyCode,
                                    userInfo.currencies,
                                    2,
                                    userInfo.decimalDelimiter,
                                    userInfo.thousandDelimiter
                                )}
                            </Typography>
                        </Box>
                    )}
                </Box>
                {!!hasTax && (
                    <Box mt={2}>
                        <Typography variant="caption" color={disabled ? "disabled" : "secondary"}>
                            Celkem k zaplacení
                        </Typography>
                        <Typography variant="h6" color={disabled ? "disabled" : "text.primary"}>
                            {formatCurrency(
                                parseNum(
                                    total,
                                    userInfo.decimalDelimiter,
                                    userInfo.thousandDelimiter
                                ) + tax,
                                currencyCode,
                                userInfo.currencies,
                                2,
                                userInfo.decimalDelimiter,
                                userInfo.thousandDelimiter
                            )}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const TransactionItemsTotalSection = ({ items, currencyCode, disabled, hasTax }) => {
    const { userInfo } = useUserInfo();
    let tax = 0;
    let total = 0;
    if (Array.isArray(items)) {
        items.forEach((item) => {
            total += item.total;
            tax += (item.taxItems || []).reduce((acc, taxItem) => acc + taxItem.total, 0);
        });
    }

    return (
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
            <Box>
                <Box>
                    <Typography variant="caption" color={disabled ? "disabled" : "secondary"}>
                        Celkem
                    </Typography>
                    <Typography variant="h6" color={disabled ? "disabled" : "text.primary"}>
                        {formatCurrency(
                            total,
                            currencyCode,
                            userInfo.currencies,
                            2,
                            userInfo.decimalDelimiter,
                            userInfo.thousandDelimiter
                        )}
                    </Typography>
                    {!!hasTax && (
                        <Box>
                            <Typography
                                variant="caption"
                                color={disabled ? "disabled" : "secondary"}>
                                + daň{" "}
                                {formatCurrency(
                                    tax,
                                    currencyCode,
                                    userInfo.currencies,
                                    2,
                                    userInfo.decimalDelimiter,
                                    userInfo.thousandDelimiter
                                )}
                            </Typography>
                        </Box>
                    )}
                </Box>
                {!!hasTax && (
                    <Box mt={2}>
                        <Typography variant="caption" color={disabled ? "disabled" : "secondary"}>
                            Celkem k zaplacení
                        </Typography>
                        <Typography variant="h6" color={disabled ? "disabled" : "text.primary"}>
                            {formatCurrency(
                                total + tax,
                                currencyCode,
                                userInfo.currencies,
                                2,
                                userInfo.decimalDelimiter,
                                userInfo.thousandDelimiter
                            )}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
